// Drone settlements — shared specifications between client and server.
// See docs/drone_settlement.md (design) and docs/mechanics/settlements.md (impl).
//
// A settlement is an emergent NPC-station-like node that nucleates out of loose
// rogue drones. Unlike NPC / private stations it has a DESTROYABLE core and NO
// PvP safe zone — players can drain it (kill its drones for loot) or destroy it
// (clear its fighters, then kill the core). On death its surviving drones revert
// to loose rogues and the cycle can begin again.

import type { DroneType } from './drones.js';

/** Runtime lifecycle phase of a forming rogue group (server-memory only, never persisted). */
export type RogueGroupPhase = 'gathering' | 'migrating';

export const SETTLEMENT_CONFIG = {
  // ==================== Accretion (pre-settlement gathering) ====================

  /** How often the accretion pass evaluates loose rogues (ms). Coarse on purpose. */
  ACCRETION_TICK_MS: 12_000,

  /**
   * Fixed probability that two eligible rogues couple when they encounter each
   * other. Low — settlements must be RARE / sporadic, not fill the map.
   * NOTE (future, not for alpha): could scale up with the count of loose rogues
   * in the constellation so settlements become a consequence of player neglect.
   */
  COUPLING_CHANCE: 0.2,

  /** Distance within which two rogues (or a rogue and a forming group) can encounter. */
  ENCOUNTER_RADIUS: 1400,

  // ==================== Group movement (manager-driven) ====================

  /** Fixed distance followers keep from their group anchor (leader). */
  FOLLOW_DISTANCE: 240,

  /** Leader drift speed while gathering (× drone speed). */
  LEADER_WANDER_SPEED_FACTOR: 0.22,

  /** Group travel speed toward the constitution point (× drone speed). */
  MIGRATE_SPEED_FACTOR: 0.5,

  /** How long a leader drifts toward one wander waypoint before repicking (ms). */
  LEADER_WANDER_LEG_MS: 9_000,

  // ==================== Minimum viable composition to constitute ====================

  /**
   * The group must reach AT LEAST this mix before it migrates + constitutes — a
   * settlement is never born without defenders. Reaching 2 fighters requires fighter
   * rogues to exist, so FighterAI rolls for rogue while idle on patrol (see FighterAI).
   * Settlement-owned haulers just idle (HaulerAI early-returns for non-NPC owners) —
   * they're part of the nest as loot, not arbitrage.
   */
  MIN_COMPOSITION: { miner: 2, hauler: 1, fighter: 2 } as Record<DroneType, number>,

  // ==================== Operating settlement ====================

  /** Target mix the replenish loop grows toward, up to DRONE_CAPACITY. */
  GROWTH_COMPOSITION: { miner: 0.5, hauler: 0, fighter: 0.5 } as Record<DroneType, number>,

  /** Hard ceiling on drones a settlement can own (server compute cost cap). */
  DRONE_CAPACITY: 12,

  /**
   * Core hull. High on purpose — the core is ALWAYS damageable (no need to clear the
   * fighters first), so its toughness + regen is what makes a grown settlement a
   * "gather a group to bring it down" objective. Tunable — drop it if solo raids
   * should be quicker.
   */
  CORE_HULL: 15000,

  /** Core HP regenerated per second once it hasn't been hit for CORE_REGEN_DELAY_MS. */
  CORE_REGEN_PER_SEC: 120,

  /** No core regen until this long after the last hit (combat tag — keeps assaults making progress). */
  CORE_REGEN_DELAY_MS: 6000,

  /** Credits seeded into the settlement pool at constitution (funds replenish/growth). */
  SEED_CREDITS: 12_000,

  /** How often a settlement evaluates whether to buy a replenishment drone (ms). */
  REPLENISH_TICK_MS: 5 * 60 * 1000,

  /** Radius around the core within which living fighters shield it from damage. */
  CORE_DEFENSE_RADIUS: 1800,

  // ==================== Constitution placement (cheap — no pathfinding) ====================

  /** Random candidate points sampled; we keep the one farthest from the nearest thing. */
  PLACEMENT_SAMPLES: 14,

  /** Margin kept from the constellation edge when sampling candidate points. */
  PLACEMENT_EDGE_MARGIN: 6000,

  /**
   * Constitution point is sampled in this distance band AROUND the group's current
   * position — NOT across the whole constellation. The constellation is ~900k units wide; sampling
   * globally always picks a far corner and the group (travelling at a fraction of
   * drone speed) would never arrive. A few-thousand-unit hop keeps it "hidden / away
   * from structures" while staying reachable and preserving the kill-it-in-the-cradle window.
   */
  CONSTITUTION_MIN_DISTANCE: 3000,
  CONSTITUTION_MAX_DISTANCE: 10000,

  // ==================== Market (dockable order book, like an NPC station) ====================

  /** Center distance within which a player may dock (settlements have a large body). */
  DOCK_RADIUS: 900,

  /** Where the docked ship is parked — just outside the settlement body. */
  DOCK_OUTER_RADIUS: 720,
} as const;

/** Default settlement tier when it can't be derived from the constellation (defensive fallback). */
export const SETTLEMENT_DEFAULT_TIER = 1;
